import React, { useEffect, useRef } from "react";
import { Bold, Heading1, Heading2, List, ListOrdered, Underline, Italic } from "lucide-react";

/**
 * Lightweight rich text editor (contentEditable + execCommand).
 * Supports: H1/H2, bold, italic, underline, bullet list, numbered list.
 * Emits HTML string via onChange.
 *
 * NOTE: We intentionally avoid heavy deps (TipTap/Slate/Lexical).
 * execCommand is deprecated but still works reliably in every modern browser.
 */
export default function RichTextEditor({ value = "", onChange, testid = "richtext", placeholder = "Start typing…" }) {
  const ref = useRef(null);

  // Only initialise once; keep contentEditable in sync only if the value
  // changes externally (e.g. record load).
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
  }, []);

  useEffect(() => {
    // If external value changes and differs from DOM, sync.
    if (ref.current && document.activeElement !== ref.current) {
      if (ref.current.innerHTML !== (value || "")) {
        ref.current.innerHTML = value || "";
      }
    }
  }, [value]);

  const exec = (cmd, arg) => {
    // Preserve selection inside editor before running.
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    handleInput();
  };

  const handleInput = () => {
    if (!ref.current) return;
    onChange?.(ref.current.innerHTML);
  };

  const btn = (icon, label, cmd, arg, tid) => (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); exec(cmd, arg); }}
      title={label}
      aria-label={label}
      data-testid={`${testid}-btn-${tid}`}
      className="h-7 w-7 flex items-center justify-center rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-300"
    >
      {icon}
    </button>
  );

  return (
    <div
      className="rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 focus-within:border-sky-400 focus-within:ring-2 focus-within:ring-sky-400/30 overflow-hidden"
      data-testid={testid}
    >
      <div className="flex items-center gap-0.5 px-1.5 py-1 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/60">
        {btn(<Heading1 className="w-3.5 h-3.5" />, "Heading 1", "formatBlock", "<h1>", "h1")}
        {btn(<Heading2 className="w-3.5 h-3.5" />, "Heading 2", "formatBlock", "<h2>", "h2")}
        <span className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1" />
        {btn(<Bold className="w-3.5 h-3.5" />, "Bold", "bold", null, "bold")}
        {btn(<Italic className="w-3.5 h-3.5" />, "Italic", "italic", null, "italic")}
        {btn(<Underline className="w-3.5 h-3.5" />, "Underline", "underline", null, "underline")}
        <span className="w-px h-4 bg-slate-200 dark:bg-slate-700 mx-1" />
        {btn(<List className="w-3.5 h-3.5" />, "Bullet list", "insertUnorderedList", null, "ul")}
        {btn(<ListOrdered className="w-3.5 h-3.5" />, "Numbered list", "insertOrderedList", null, "ol")}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={handleInput}
        onBlur={handleInput}
        data-placeholder={placeholder}
        data-testid={`${testid}-editor`}
        className="min-h-[128px] px-3 py-2 text-sm text-slate-800 dark:text-slate-100 focus:outline-none prose prose-sm max-w-none prose-headings:mt-2 prose-headings:mb-1 prose-p:my-1 prose-ul:my-1 prose-ol:my-1 empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400"
      />
    </div>
  );
}
